import React, {
    Dispatch,
    SetStateAction,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { useNavigate, useParams, usePrompt } from 'react-router-dom'
import { axiosGet, axiosPatch, axiosPost } from '../util/api'
import deepEqual from 'fast-deep-equal'
import { useUser } from '../hooks/useUser'
import { Toasts } from '../components/toaster/Toast'
import { PromotionEditorNew } from '../components/promotions/PromotionEditorNew'
import {
    DefaultPromotion,
    groupItems,
    normalizePromotionRuleOperators,
    sortItems,
} from '../util/promotions'
import { SetItems } from '../types/promotions'
import { useRecoilValue, useResetRecoilState } from 'recoil'
import { PromotionRule } from '../components/promotions/PromotionRule'
import { activeRuleAtom } from '../components/promotions/promotion_state'
import PromotionItemI = Models.PromotionItemI
import PromotionI = Models.Promotion
import PromotionRuleI = Models.PromotionRuleI

/**
 * Mirror the validation that `AddPromotionRequest` performs server-side, plus
 * the implicit assumptions that crash its `authorize()` (missing campaign_id /
 * ad_group_id on an item) and `Promotion::syncRuleItems()` (rule item that
 * references a (campaign, ad_group) pair not in `items`).
 *
 * Keep the same guard client-side so the editor can surface a specific reason
 * before the server performs its authoritative entity-pair validation.
 */
export interface PromotionValidation {
    isValid: boolean
    reason?: string
}

const itemKey = (item: {
    campaign_id?: number | string | null
    ad_group_id?: number | string | null
}) => `${item.campaign_id}:${item.ad_group_id}`

const truthyFlag = (value: unknown): boolean =>
    value === true || value === 'true' || value === 1 || value === '1'

const ruleItemHasSource = (ruleItem: any): boolean => truthyFlag(ruleItem.use_search_terms)

const ruleItemHasDestination = (ruleItem: any): boolean =>
    truthyFlag(ruleItem.promotion_broad) ||
    truthyFlag(ruleItem.promotion_phrase) ||
    truthyFlag(ruleItem.promotion_exact) ||
    truthyFlag(ruleItem.promotion_products) ||
    truthyFlag(ruleItem.negation_phrase) ||
    truthyFlag(ruleItem.negation_exact) ||
    truthyFlag(ruleItem.negation_products)

export const sanitizePromotionRules = (
    items: PromotionItemI[],
    rules: PromotionRuleI[],
): PromotionRuleI[] => {
    const itemKeys = new Set(items.map(itemKey))

    return (rules ?? []).map((rule) => ({
        ...rule,
        rule_items: (rule.rule_items ?? []).filter(
            (ruleItem: any) =>
                ruleItem.campaign_id && ruleItem.ad_group_id && itemKeys.has(itemKey(ruleItem)),
        ),
    }))
}

export const validatePromotionPayload = (
    promotion: PromotionI,
    items: PromotionItemI[],
    rules: PromotionRuleI[],
): PromotionValidation => {
    const name = (promotion.name ?? '').trim()
    if (name.length === 0)
        return { isValid: false, reason: 'Give the campaign a name before saving' }
    if (name.length > 255) return { isValid: false, reason: 'Name must be 255 characters or fewer' }

    if (items.length === 0) {
        return { isValid: false, reason: 'Link at least one ad group before saving' }
    }
    for (const item of items) {
        if (!item.campaign_id || !item.ad_group_id) {
            return {
                isValid: false,
                reason: 'One of the linked ad groups is incomplete — remove and re-add it',
            }
        }
    }

    const itemKeys = new Set(items.map(itemKey))
    for (const rule of rules ?? []) {
        const ruleLabel = rule.name ? `"${rule.name}"` : 'A rule'

        const bidFields = [
            (rule as any).bid_increase_amount,
            (rule as any).bid_increase_percent,
            (rule as any).bid_set,
        ].filter((v) => v !== null && v !== undefined && v !== '')
        if (bidFields.length > 1) {
            return {
                isValid: false,
                reason: `${ruleLabel} sets more than one bid action — pick one`,
            }
        }

        const ruleItems = (rule as any).rule_items ?? []
        if (ruleItems.length === 0) {
            return {
                isValid: false,
                reason: `${ruleLabel} needs at least one source and one destination`,
            }
        }

        let hasSource = false
        let hasDestination = false
        for (const ri of ruleItems) {
            if (!ri.campaign_id || !ri.ad_group_id) {
                return { isValid: false, reason: `${ruleLabel} targets an incomplete ad group` }
            }
            if (!itemKeys.has(itemKey(ri))) {
                return {
                    isValid: false,
                    reason: `${ruleLabel} targets an ad group that isn't linked to this campaign`,
                }
            }
            hasSource = hasSource || ruleItemHasSource(ri)
            hasDestination = hasDestination || ruleItemHasDestination(ri)
        }

        if (!hasSource) {
            return { isValid: false, reason: `${ruleLabel} needs at least one source ad group` }
        }
        if (!hasDestination) {
            return {
                isValid: false,
                reason: `${ruleLabel} needs at least one promotion or negation destination`,
            }
        }
    }

    return { isValid: true }
}

export const Promotion = ({}) => {
    const navigate = useNavigate()
    const [saving, setSaving] = useState<boolean>(false)
    const { promotion_id } = useParams()
    const [promotion, setPromotion] = useState<PromotionI>(DefaultPromotion)
    const [items, setItemsRaw] = useState<PromotionItemI[]>([])
    const [rules, setRules] = useState<PromotionRuleI[]>([])
    const [loadingPromotion, setLoadingPromotion] = useState<boolean>(
        !!promotion_id && promotion_id !== 'new',
    )
    // Ref (not state) so it's always current — no stale-closure or batching concerns.
    // For new promotions we know the baseline immediately; for existing ones we set it
    // synchronously inside the load callback before any setState calls fire.
    const initialDataRef = useRef<{
        promotion: PromotionI
        items: PromotionItemI[]
        rules: PromotionRuleI[]
    } | null>(
        promotion_id === 'new'
            ? { promotion: { ...DefaultPromotion }, items: [], rules: [] }
            : null,
    )
    const [dirty, setDirty] = useState<boolean>(false)
    const [redirect, setRedirect] = useState<boolean>(false)
    const { hasActiveSubscription, screenshotMode } = useUser()
    const activeRule = useRecoilValue(activeRuleAtom)
    const resetActiveRule = useResetRecoilState(activeRuleAtom)

    // Always land on the campaign detail page — clear any leftover rule state
    // from a previous session so we don't accidentally drop into the rule editor.
    useEffect(() => {
        resetActiveRule()
    }, [])

    const setItems = useCallback(
        (items: SetItems) => {
            if (!items) {
                return
            }

            if (typeof items == 'function') {
                setItemsRaw((p) => items(p).sort(sortItems))
            } else {
                setItemsRaw(items.sort(sortItems))
            }
        },
        [setItemsRaw],
    )

    usePrompt('Are you sure you want to leave? Your changes will not be saved', dirty)

    useEffect(() => {
        if (promotion_id !== 'new' && promotion_id != '') {
            load(parseInt(promotion_id))
        }
    }, [])

    // This is a hacky workaround to allow a redirect after a save
    // In the successful save callback, we set dirty to false and
    // redirect to true, which then gets picked up by this hook to
    // perform the actual redirect.
    useEffect(() => {
        if (redirect && !dirty) {
            navigate('/smart-campaigns')
        }
    }, [redirect, dirty])

    useEffect(() => {
        const snap = initialDataRef.current
        if (!snap) {
            // Still loading an existing promotion — suppress the save bar.
            setDirty(false)
            return
        }
        setDirty(
            !deepEqual(promotion, snap.promotion) ||
                !deepEqual(items, snap.items) ||
                !deepEqual(rules, snap.rules),
        )
    }, [promotion, items, rules])

    const load = (id: number) => {
        setLoadingPromotion(true)
        axiosGet(`promotions/${id}`)
            .then((res) => {
                const sortedItems = groupItems(res.data.items || []).sort(sortItems)
                const loadedRules = (res.data.rules || []).map((rule: any) =>
                    normalizePromotionRuleOperators({
                        ...rule,
                        spend_value: rule.spend_value ? rule.spend_value / 100 : rule.spend_value,
                    }),
                )
                const sanitizedRules = sanitizePromotionRules(sortedItems, loadedRules)
                const promotionData: PromotionI = { ...res.data, items: sortedItems }

                // Write the snapshot synchronously BEFORE any setState calls so the
                // dirty effect always reads the correct baseline, regardless of how
                // React batches the subsequent state updates.
                initialDataRef.current = {
                    promotion: promotionData,
                    items: sortedItems,
                    rules: loadedRules,
                }

                if (!deepEqual(sanitizedRules, loadedRules)) {
                    Toasts.warning(
                        'Some rule destinations referenced unlinked ad groups and need to be saved or fixed.',
                    )
                }

                setPromotion(promotionData)
                setRules(sanitizedRules)
                setItemsRaw(sortedItems)
            })
            .finally(() => {
                setLoadingPromotion(false)
            })
    }

    const setPromotionValue =
        <K extends keyof PromotionI>(key: K) =>
        (value: PromotionI[K]) => {
            setPromotion((prev) => ({
                ...prev,
                [key]: value,
            }))
        }

    const validation = useMemo(
        () => validatePromotionPayload(promotion, items, sanitizePromotionRules(items, rules)),
        [promotion, items, rules],
    )

    const save = () => {
        const sanitizedRules = sanitizePromotionRules(items, rules)

        // Preflight obvious payload-shape errors. The server still performs
        // authoritative backend-specific entity-pair validation.
        const sanitizedValidation = validatePromotionPayload(promotion, items, sanitizedRules)
        if (!sanitizedValidation.isValid) {
            Toasts.error(sanitizedValidation.reason ?? 'This Smart Campaign is incomplete')
            return
        }

        if (!deepEqual(sanitizedRules, rules)) {
            setRules(sanitizedRules)
        }

        const payload: Record<string, any> = {
            ...promotion,
            rules: sanitizedRules.map((rule) => normalizePromotionRuleOperators({ ...rule })),
            items,
        }
        Object.keys(payload).forEach((key) => {
            if (payload[key] === '') {
                payload[key] = null
            }
        })

        setSaving(true)
        let promise: Promise<any>
        const editing = promotion_id && promotion_id !== 'new'
        if (editing) {
            promise = axiosPatch(`promotions/${promotion_id}`, payload)
        } else {
            promise = axiosPost('promotions', payload)
        }

        promise
            .then(() => {
                setSaving(false)
                Toasts.success(`Smart Campaign ${editing ? 'saved' : 'created'}`)

                setDirty(false)
                setRedirect(true)
            })
            .catch((err) => {
                // The shared axios handler in util/api.tsx already toasts the
                // response message; log here so anything it suppressed is still
                // visible in the console for debugging.
                console.error('Failed to save promotion', err)
                setSaving(false)
            })
    }

    if (activeRule) {
        return (
            <PromotionRule
                promotion={promotion}
                initialRule={activeRule}
                onSave={(rule) => {
                    onRuleSave(setRules, rule)
                }}
            />
        )
    }

    return (
        <PromotionEditorNew
            promotionId={promotion_id}
            promotion={promotion}
            items={items}
            rules={rules}
            setItems={setItems}
            setRules={setRules}
            screenshotMode={screenshotMode}
            dirty={dirty}
            saving={saving}
            loading={loadingPromotion}
            isNew={promotion_id === 'new'}
            hasActiveSubscription={hasActiveSubscription}
            validation={validation}
            onSave={save}
            onCancel={() => navigate('/smart-campaigns')}
            setPromotionValue={setPromotionValue}
        />
    )
}

export const onRuleSave = (
    setRules: Dispatch<SetStateAction<PromotionRuleI[]>>,
    rule: PromotionRuleI,
) => {
    if (rule.id) {
        setRules((prev) =>
            prev.map((i) => {
                if (i.id == rule.id) {
                    return {
                        ...i,
                        ...rule,
                    }
                } else {
                    return i
                }
            }),
        )
    } else if (rule.uuid) {
        setRules((prev) => {
            let found = false
            prev = prev.map((i) => {
                if (i.uuid == rule.uuid) {
                    found = true
                    return {
                        ...i,
                        ...rule,
                    }
                } else {
                    return i
                }
            })
            if (!found) {
                prev = [...prev, rule]
            }
            return prev
        })
    } else {
        setRules((prev) => [...prev, rule])
    }
}
